import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO'; // ✅ Add this line
import reports from "./ReportConfig";
import ReportCard from "./ReportCard";


const BASE_URL = process.env.REACT_APP_BASE_URL || '';
const WEBSITE_NAME = process.env.REACT_APP_WEBSITE_NAME || 'myBOQ';
const SHOW_FREE_MONTH = process.env.REACT_APP_SHOW_FREE_MONTH === 'true';


const partners = [
  {
    name: "CEIIT",
    image: "/CEIIT_LOGO.png",
    description: "Enhance your skills in civil engineering estimations with expert-led training and workshops.",
    link: "https://civilengineeringiit.in/"
  }
];

const HeroSection = () => {
  return (
    <section className="bg-white py-12 px-6 lg:px-20">
      {/* ✅ SEO Metadata for Homepage or Landing */}
      <SEO
         title={`${WEBSITE_NAME} | Civil Estimation Software with SSR Precision`}
        description="Simplify estimation and billing with myBOQ. Get accurate PWD SSR-based tenders, bills, and reports in minutes. Built for engineers, contractors, and consultants."
        keywords="civil estimation software, SSR BOQ, tender generator, PWD estimate tool,BOQ,CIVIL, estimate ,india , maharashtra,pwd,bank estimate,measurement,civil engineering,siliconmount,CEIIT"
        image={`${BASE_URL}/ssrlogo3.png`}
        url={BASE_URL}
      />

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
        {/* Headline */}
        <div className="space-y-6">
          <h1 className="text-4xl lg:text-4xl font-bold text-gray-900 leading-snug">
            <span className="text-orange-600">myBOQ </span> 
            Estimations <br className="hidden lg:block" />
            Simplified with <span className="text-orange-600">PWD SSR Precision</span>
          </h1>
          <p className="text-gray-700 text-lg">
            Prepare accurate estimates, tenders, and bills faster—without missing any compliance details.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mt-6">
          <a
    href="https://www.youtube.com/watch?v=waJAgQUF6j8&t=292s"
    target="_blank"
    rel="noopener noreferrer"
    className="bg-gray-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:bg-orange-600 transition duration-300 flex items-center gap-2"
    >
      {/* YouTube Icon */}
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 22v-20l18 10-18 10z" />
      </svg> Watch Demo Video
  </a>
            <a
              href={`${BASE_URL}/myBOQ_Software_Brochure.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold hover:bg-orange-300 transition duration-300"
            >
              Download Brochure
            </a>
            <Link
              to="/signup"
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold transition duration-300"
            >
              Get Started
            </Link>  
          </div>
         
        </div>

        {/* Partner Card */}
        <div className="pl-4 lg:pl-12 max-w-sm mx-auto lg:mx-0">
          <h3 className="text-xl font-semibold text-gray-700 mb-4 text-center lg:text-left">Our Partners</h3>
          {partners.map((partner, idx) => (
            <div key={idx} className="bg-gray-50 shadow-md rounded-lg overflow-hidden p-6 text-center hover:shadow-lg transition duration-300">
              <img
                src={partner.image}
                alt={`${partner.name} official logo`}
                className="w-20 h-20 object-contain mx-auto mb-4"
              />
              <p className="text-gray-600 mb-2 text-sm">{partner.description}</p>
              <a
                href={partner.link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-600 font-semibold hover:underline text-sm"
              >
                Visit {partner.name} →
              </a>
            </div>
          ))}
        </div>
      </div>
  {/* Hero Report Preview with Inline CTA */}
  <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Reports at a Glance
            </h3>
            <div className="flex flex-wrap gap-4">
              {reports.map((report, idx) => (
                <ReportCard key={idx} {...report} compact />
              ))}
            </div>
          </div>
      {/* Built For Section */}
      <div className="mt-20 max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
          Built For Professionals
        </h2>
        <div className="flex flex-wrap justify-center gap-6">
          {[
          { icon: '/engineer.svg', label: 'Engineers' },
          { icon: '/contractor.svg', label: 'Contractors' },
          { icon: '/architect.svg', label: 'Architects' },
          { icon: '/municipality.svg', label: 'Municipal Staff' },
          { icon: '/consultant.svg', label: 'Consultants' },
          ].map((item, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center text-center bg-white shadow-md rounded-xl p-5 w-40 transition transform hover:scale-105"
            >
              <img src={item.icon} alt={`${item.label} icon`} className="w-12 h-12 mb-3" />
              <h4 className="text-md font-semibold text-gray-700">{item.label}</h4>
            </div>
          ))}
        </div>
      </div>

      {/* Feature Highlights */}
      <div className="mt-20 max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
          Feature Highlights
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
        { icon: '/fast.svg', text: 'Fast & Accurate Estimations' },
        { icon: '/subscription.png', text: 'Subscription = Flexibility + Savings', isSubscription: true },
        { icon: '/ssr.svg', text: 'SSR & Non-SSR Item Support' },
        { icon: '/analysis.svg', text: 'Auto Rate Analysis' },
        { icon: '/duplicate.png', text: 'Duplicate Revision & Estimates' },
          ].map((feature, idx) => (
            <div
              key={idx}
              className="flex flex-col items-center text-center bg-orange-50 shadow-md rounded-xl p-6 transition hover:shadow-lg"
            >
              <img src={feature.icon} alt={feature.text} className="w-10 h-10 mb-4" />
              <p className="text-gray-700 font-medium text-sm">{feature.text}</p>
              {feature.isSubscription && (
                <Link
                  to="/pricing"
                  className="mt-2 inline-block bg-orange-500 text-white text-xs px-4 py-2 rounded-full font-semibold hover:bg-orange-600 transition"
                >
                  View Plans & Pricing
                </Link>
              )}
            </div>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Link to="/features" className="text-orange-600 font-semibold hover:underline text-sm">
            View Full Features →
          </Link>
        </div>
      </div>
   

    </section>
  );
};

export default HeroSection;
